import { css } from '@emotion/react';
import { IconButton, Stack, SvgIcon, Tooltip, Typography } from '@mui/material';
import { ComponentType, SVGProps, useMemo } from 'react';
import CrunchbaseIcon from '../../../assets/icons/Crunchbase.svg?react';
import FacebookIcon from '../../../assets/icons/fb.svg?react';
import LinkedinIcon from '../../../assets/icons/ln.svg?react';
import PitchbookIcon from '../../../assets/icons/pitchbook.svg?react';
import TwitterIcon from '../../../assets/icons/tw.svg?react';
import { ISourcingCompanyRow } from '../../../schemas/SourcingListItems.schema';
import { DetailRow } from './DetailRow';
import { detailLabelCss, detailRowCss, sectionCss, sectionTitleCss } from './sourcing-detail-panel.styles';

const profileIconCss = css`
  width: 20px;
  height: 20px;
  border-radius: 100px;
`;

interface Profile {
  readonly label: string;
  readonly url: string | null;
  readonly Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

export interface CompanyOverviewSectionProps {
  readonly data: ISourcingCompanyRow;
}

export function CompanyOverviewSection({ data }: CompanyOverviewSectionProps) {
  const profiles = useMemo<Profile[]>(
    () => [
      { label: 'LinkedIn', url: data.linkedinUrl, Icon: LinkedinIcon },
      { label: 'Crunchbase', url: data.crunchbaseUrl, Icon: CrunchbaseIcon },
      { label: 'PitchBook', url: data.pitchbookUrl, Icon: PitchbookIcon },
      { label: 'Twitter / X', url: data.twitterUrl, Icon: TwitterIcon },
      { label: 'Facebook', url: data.facebookUrl, Icon: FacebookIcon },
    ],
    [data.linkedinUrl, data.crunchbaseUrl, data.pitchbookUrl, data.twitterUrl, data.facebookUrl]
  );

  return (
    <Stack css={sectionCss} gap='4px'>
      <Typography css={sectionTitleCss}>Company Overview</Typography>
      <Stack gap='4px'>
        <DetailRow label='Short Description' value={data.shortDescription} />
        <DetailRow label='Sector' value={data.sector} />
        <DetailRow label='CEO' value={data.ceoName} />
        <DetailRow label='Founders' value={data.founders?.join(', ')} />
        <DetailRow label='Website' value={data.website} isLink />
        <ProfilesRow profiles={profiles} />
      </Stack>
    </Stack>
  );
}

function ProfilesRow({ profiles }: { readonly profiles: Profile[] }) {
  const present = profiles.filter((p): p is Profile & { url: string } => p.url != null);
  if (present.length === 0) {
    return null;
  }

  return (
    <div css={detailRowCss}>
      <Typography css={detailLabelCss}>Profiles</Typography>
      <Stack direction='row' gap='4px' justifyContent='flex-end' flex={1}>
        {present.map(({ label, url, Icon }) => (
          <Tooltip key={label} title={label}>
            <IconButton
              size='small'
              component='a'
              href={url}
              target='_blank'
              rel='noopener noreferrer'
              css={profileIconCss}
            >
              <SvgIcon sx={{ fontSize: 18 }} viewBox='0 0 20 20'>
                <Icon />
              </SvgIcon>
            </IconButton>
          </Tooltip>
        ))}
      </Stack>
    </div>
  );
}
